<template>
  <TabbedBox>
    <template v-slot:tablist>
      <Tab :first="true" tabid="tab-categories">
        <i class="far fa-layer-group"></i> Stock Categories
      </Tab>
      <Tab tabid="tab-containers">
        <i class="far fa-container-storage"></i> Stock Containers
      </Tab>
      <Tab tabid="tab-inventory-bins">
        <i class="far fa-inventory"></i> Inventory Bins
      </Tab>
      <Tab tabid="tab-inventory-settings">
        <i class="far fa-cogs"></i> Inventory Settings
      </Tab>
      <Tab tabid="tab-activity">
        <i class="far fa-history"></i> Activity Log
      </Tab>
    </template>
    <template v-slot:tabcontent>
      <TabPage :first="true" tabid="tab-categories">
        <div class="row">
          <div class="col-lg-12">
            <div v-if="(userHasCreatePermission(PermissionTypes.InventoryPermissionTypes.inventoryConfig) || userHasUpdatePermission(PermissionTypes.InventoryPermissionTypes.inventoryConfig)) && !stockCategoryEditing" style="clear: both;">
              <ActionDropMenu class="float-right">
                <DropdownMenuItem @click="newStockCategory(null)" v-if="userHasCreatePermission(PermissionTypes.InventoryPermissionTypes.inventoryConfig)" icon="fa-plus">Add Category</DropdownMenuItem>
                <DropdownMenuItem @click="startCategoryEditing()" v-if="userHasUpdatePermission(PermissionTypes.InventoryPermissionTypes.inventoryConfig)" icon="fa-sort-amount-up-alt">Edit Category Order</DropdownMenuItem>
                <DropdownMenuDivider />
                <DropdownMenuItem @click="stockCategoryShowStock = true" icon="fa-eye" v-if="!stockCategoryShowStock">Show Stock</DropdownMenuItem>
                <DropdownMenuItem @click="stockCategoryShowStock = false" icon="fa-eye-slash" v-if="stockCategoryShowStock">Hide Stock</DropdownMenuItem>
              </ActionDropMenu>
            </div>
            <ContentTitle>Stock Category List</ContentTitle>
          </div>
        </div>
        <div class="row">
          <div class="col-lg-12">
            <FormGroup>
              <NestedList :listTree="nestedCategoryList" :editingOrder="stockCategoryEditing" @change="nestedCategoryList = $event">
                <template v-slot:itemSlot="slotProps">
                  <span v-if="slotProps.item.isCategory && !slotProps.item.isActive && !stockCategoryEditing" class="mx-2">
                    <i class="fas fa-times status-inactive font-weight-bold"></i>
                  </span>
                  
                  <ActionDropMenu class="float-right" :tableAction="true" v-if="slotProps.item.isCategory">
                    <DropdownMenuItem @click="editStockCategory(slotProps.item)" icon="fa-pencil">Edit Category</DropdownMenuItem>
                    <DropdownMenuItem @click="newStockCategory(slotProps.item.id, slotProps.item.stockType)" icon="fa-plus">Add Sub Category</DropdownMenuItem>
                    <DropdownMenuItem @click="setStockCategoryActive(slotProps.item.id, false)" icon="fa-times" v-if="slotProps.item.isActive">Make Inactive</DropdownMenuItem>
                    <DropdownMenuItem @click="setStockCategoryActive(slotProps.item.id, true)" icon="fa-check" v-if="!slotProps.item.isActive">Make Active</DropdownMenuItem>
                  </ActionDropMenu>
                  <span v-if="slotProps.item.isCategory">{{slotProps.item.name}}</span>
                  <LinkStock v-if="slotProps.item.isStock" :id="slotProps.item.id" :name="`${slotProps.item.code} - ${slotProps.item.name}`" />
                </template>
              </NestedList>

              <div v-if="stockCategoryEditing">
                <FormHorizontalRule />
                <FormSubmit label="Update" :submitting="stockCategorySubmitting" @cancelled="cancelCategoryEditing" @submitted="submitCategoryEditing"></FormSubmit>
              </div>
            </FormGroup>
          </div>
        </div>
      </TabPage>

      <TabPage tabid="tab-containers">
        <div class="row">
          <div class="col-lg-12">
            <div v-if="(userHasCreatePermission(PermissionTypes.InventoryPermissionTypes.inventoryConfig) || userHasUpdatePermission(PermissionTypes.InventoryPermissionTypes.inventoryConfig)) && !stockContainerEditing" style="clear: both;">
              <ActionDropMenu class="float-right">
                <DropdownMenuItem @click="newStockContainer" v-if="userHasCreatePermission(PermissionTypes.InventoryPermissionTypes.inventoryConfig)" icon="fa-plus">New Stock Containers </DropdownMenuItem>
                <DropdownMenuItem @click="stockContainerEditing=true" v-if="userHasUpdatePermission(PermissionTypes.InventoryPermissionTypes.inventoryConfig)" icon="fa-sort-amount-up-alt">Edit Stock Container Order </DropdownMenuItem>
              </ActionDropMenu>
            </div>
            <ContentTitle>Stock Container Types</ContentTitle>
          </div>
        </div>
        <div class="row">
          <div class="col-lg-12">
            <StaticDataTable class="static-table-list" :editing="stockContainerEditing" :key="stockContainerKey" @sortUpdated="stockContainerOrder = $event" >
              <template v-slot:header>
                <tr>
                  <th v-if="stockContainerEditing"></th>
                  <th>Code</th>
                  <th>Name</th>
                  <th class="text-center">Active</th>
                  <th>Block Quantity</th>
                  <th>Actions</th>
                </tr>
              </template>
              <template v-slot:body>
                <tr v-for="stockContainer in stockContainerList" :key="stockContainer.id" :data-sort-id="stockContainer.id" class="static-table-sortable">
                  <td v-if="stockContainerEditing" width="16px"><div class="static-datatable-row-draggable"><i class="fal fa-bars"></i> </div></td>
                  <td class="font-weight-bold">{{stockContainer.code}}</td>
                  <td>{{stockContainer.name}}</td>
                  <td class="text-center" v-html="dataDisplayTableBoolean(stockContainer.isActive, { showFalse: true })"></td>
                  <td>{{stockContainer.blockQuantity}}</td>
                  <td>
                    <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.InventoryPermissionTypes.inventoryConfig)" :tableAction="true">
                      <DropdownMenuItem @click="editStockContainer(stockContainer)" icon="fa-pencil">Edit Container</DropdownMenuItem>
                      <DropdownMenuItem @click="setStockContainerActive(stockContainer.id, false)" icon="fa-times" v-if="stockContainer.isActive">Make Inactive</DropdownMenuItem>
                      <DropdownMenuItem @click="setStockContainerActive(stockContainer.id, true)" icon="fa-check" v-if="!stockContainer.isActive">Make Active</DropdownMenuItem>
                    </ActionDropMenu>
                  </td>
                </tr>
              </template>
            </StaticDataTable>

            <FormGroup>
              <div v-if="stockContainerEditing">
                <FormHorizontalRule />
                <FormSubmit label="Update" :submitting="stockContainerSubmitting" @cancelled="cancelStockContainerEditing" @submitted="submitStockContainerEditing"></FormSubmit>
              </div>
            </FormGroup>
          </div>
        </div>
      </TabPage>

      <TabPage tabid="tab-inventory-bins">
        <DataTable
          ref="inventoryBinDataTable"
          :columns="inventoryBinColumnList"
          defaultColumnOrderDir="ASC"
          :defaultColumnOrderIndex="0"
          :url="inventoryBinDataURL"
          :filterOptions="inventoryBinFilterOptions"
          :displayTools="true"
          :displayLocationFilter="true"
          :displayExportTools="true"
          exportTitle="Inventory Bins"
        >
          <template v-slot:title>
            <ContentTitle>Inventory Bin List</ContentTitle>
            <ContentSubTitle>List of inventory bins available to put stock and production items</ContentSubTitle>
          </template>
          <template v-slot:tools>
            <ActionButton type="new" v-if="userHasCreatePermission(PermissionTypes.InventoryPermissionTypes.inventoryConfig)" @click="addInventoryBin" class="mr-2"> Add Inventory Bin</ActionButton>
            <ActionButton type="new" v-if="userHasCreatePermission(PermissionTypes.InventoryPermissionTypes.inventoryConfig)" @click="addInventoryBinBatch"> Add Batch</ActionButton>
          </template>
          <template v-slot:row="row">
            <tr :key="row.data.id">
              <td class="table-data-nav-link">
                {{row.data.barcode}}
              </td>
              <td>
                {{row.data.code}}
              </td>
              <td>
                {{row.data.description}}
              </td>
              <td>
                <IconBoolean :value="row.data.isActive" />
              </td>
              <td>
                {{formatHyphenEmptyValue(row.data.zone)}}
              </td>
              <td>
                {{formatHyphenEmptyValue(row.data.aisle)}}
              </td>
              <td>
                {{formatHyphenEmptyValue(row.data.bay)}}
              </td>
              <td>
                {{formatHyphenEmptyValue(row.data.level)}}
              </td>
              <td>
                <LabelTags :labelList="row.data.labelList" />
              </td>
              <td>
                <LabelAccountLocationBadge :accountLocationId="row.data.accountLocationId" />
              </td>
              <td>
                <ActionDropMenu :tableAction="true" :forceDropdown="true">
                  <DropdownMenuItem @click="editInventoryBin(row.data)" icon="fa-pencil" v-if="userHasUpdatePermission(PermissionTypes.InventoryPermissionTypes.inventoryConfig)">Edit Inventory Bin</DropdownMenuItem>
                  <DropdownMenuItem @click="setInventoryBinActive(row.data, false)" icon="fa-times" v-if="row.data.isActive && userHasUpdatePermission(PermissionTypes.InventoryPermissionTypes.inventoryConfig)">Make Inactive</DropdownMenuItem>
                  <DropdownMenuItem @click="setInventoryBinActive(row.data, true)" icon="fa-check" v-if="!row.data.isActive && userHasUpdatePermission(PermissionTypes.InventoryPermissionTypes.inventoryConfig)">Make Active</DropdownMenuItem>
                  <DropdownMenuDivider v-if="userHasUpdatePermission(PermissionTypes.InventoryPermissionTypes.inventoryConfig)" />
                  <DropdownMenuItem @click="printInventoryBinLabel(row.data)" icon="fa-print">Print Inventory Bin Label</DropdownMenuItem>
                  <DropdownMenuItem @click="viewInventoryBinContents(row.data)" icon="fa-layer-group">View Bin Contents</DropdownMenuItem>                  
                </ActionDropMenu>
              </td>
            </tr>
          </template>
        </DataTable>
      </TabPage>

      <TabPage tabid="tab-inventory-settings">
        <div class="row">
          <div class="col-lg-12">
            <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.InventoryPermissionTypes.inventoryConfig) && !inventorySettings.editing" class="float-right">
              <DropdownMenuItem @click="inventorySettings.editing = true" icon="fa-pencil">Edit Inventory Settings</DropdownMenuItem>
            </ActionDropMenu>
            <ContentTitle>Inventory Settings</ContentTitle>
            <ContentSubTitle>General settings for the Inventory module</ContentSubTitle>
          </div>
        </div>
        <FormGroup :labelColumnSize="6" :form="inventorySettings.form">
          <FormAccountSetting label="Glass Blocks Enabled" helpDefault="Enabled">
            <template v-slot:helpContent>
              <p>Enabling this value will convert received blocks of glass stock in purchase orders into individual blocks in the system.<br />
              If disabled, received glass stock will be converted directly into glass sheets.</p>
              <p>Existing glass blocks will not be effected</p>
            </template>
            <template v-slot:entry>
              <IconBoolean v-if="!inventorySettings.editing" :value="getConfigSettingBoolean(AccountSettingKeys.enableGlassBlocks)" />
              <FormCheckbox v-if="inventorySettings.editing" :value="getConfigSettingBoolean(AccountSettingKeys.enableGlassBlocks)" @change="setConfigSetting(AccountSettingKeys.enableGlassBlocks, $event)" />
            </template>
          </FormAccountSetting>
          
          <FormSubmit v-if="inventorySettings.editing" label="Update" :submitting="inventorySettings.submitting" @submitted="submitInventoryConfigSettings" @cancelled="cancelInventoryConfigSettings" />
        </FormGroup>
      </TabPage>

      <TabPage tabid="tab-activity">
        <div class="row">
          <div class="col-lg-12">
            <ActivityLogStream ref="activityStream" url="/inventory/config/activitystream">
              <template v-slot:title><ContentTitle>Recent Activity</ContentTitle></template>
            </ActivityLogStream>
          </div>
        </div>
      </TabPage>
    </template>
  </TabbedBox>
</template>

<script>
import _ from 'lodash'
import form from '@/mixins/form'
import TabbedBox from "@/components/layout/tabbedBox.vue"
import Tab from "@/components/layout/tab.vue"
import TabPage from "@/components/layout/tabPage.vue"
import StaticDataTable from "@/components/table/staticDataTable.vue"
import DataDisplayMixin from '@/mixins/dataDisplay'
import StockFormatterMixin from '@/modules/inventory/mixins/stockFormatter.js'

import StockCategoryDialog from '@/modules/inventory/views/stockCategoryDialog.vue'
import StockContainerDialog from '@/modules/inventory/views/stockContainerDialog.vue'
import InventoryBinDialog from '@/modules/inventory/views/inventoryBinDialog.vue'
import InventoryBinBatchDialog from '@/modules/inventory/views/inventoryBinBatchDialog.vue'
import InventoryBinContentsDialog from '@/modules/inventory/views/inventoryBinContentsDialog.vue'

import NestedList from '@/components/table/nestedList.vue'

import { AccountSettingKeys } from '@/store/types'

export default {
  name: "InventoryConfiguration",
  mixins: [form, DataDisplayMixin, StockFormatterMixin],
  components: {
    TabbedBox,
    Tab,
    TabPage,
    StaticDataTable,
    NestedList
  },
  data: () => {
    return {
      stockList: [],
      stockCategoryList: [],

      nestedCategoryList: [],
      stockCategoryEditing: false,
      stockCategorySubmitting: false,
      stockCategoryShowStock: false,

      stockContainerEditing: false,
      stockContainerSubmitting: false,
      stockContainerOrder: [],
      stockContainerKey: 0,
      stockContainerList: [],

      inventoryBinDataURL: 'inventory/bins?',
      inventoryBinColumnList: [
        { title: "Barcode", data: "id" },
        { title: "Code", data: "code" },
        { title: "Description", data: "description" },
        { title: "Active", data: "isActive", type: 'boolean' },
        { title: "Zone", data: "zone" },
        { title: "Aisle", data: "aisle" },
        { title: "Bay", data: "bay" },
        { title: "Level", data: "level" },
        { title: "Labels", data: "labelList", type: 'label' },
        { title: "Location", data: "accountLocationName" },
        { title: "Actions", data: "", sortable: false, export: false },
      ], 

      inventorySettings: {
        editing: false,
        submitting: false,
        form: {},
        settings: {}
      },

      accountLocations: []
    }
  },
  watch: {
    stockCategoryShowStock: function() {
      this.$data.nestedCategoryList = this.getNestedStockCategoryList(null)
    }
  },
  created() {
    this.AccountSettingKeys = AccountSettingKeys
  },
  mounted() {
    this.requestData()
  },
  computed: {
    nestedStockCategoryList() {
      // Calculate nested stuff
      return this.getNestedStockCategoryList(null)
    },
    inventoryBinFilterOptions() {
      var filterOptions = []

      let inventoryBinLabels = this.$store.getters['label/getLabelsByType']('inventoryBin')
      filterOptions.push({
        name: 'Bin Label',
        field: 'labelListNames',
        operator: 'like',
        options: inventoryBinLabels.reverse().map(labelInfo => {
          return {
            name: labelInfo.name,
            value: '%' + labelInfo.name + '%',
            colour: labelInfo.colour
          }
        })
      })

      return filterOptions
    }
  },
  methods: {
    reloadData() {
      this.$refs.activityStream.reloadData()
      this.$refs.inventoryBinDataTable.reload()
      this.requestData()
    },
    requestData() {
      Promise.all([
        this.$apiService.inventoryService.getStockList(),
        this.$apiService.inventoryService.getStockCategoryList(),
        this.$apiService.inventoryService.getConfigSettings(),
        this.$apiService.adminService.getAccountLocations(),
      ])
      .then(results => {
        this.$data.stockList = results[0].data
        this.$data.stockCategoryList = results[1].data

        this.$data.nestedCategoryList = this.getNestedStockCategoryList(null)

        this.$data.inventorySettings.settings = results[2].data

        this.accountLocations = results[3].data.filter(location => location.isActive)
      })
      this.$apiService.inventoryService.getStockContainerList().then(response => {
        this.$data.stockContainerList = response.data
        this.$data.stockContainerOrder = this.$data.stockContainerList.map(container => container.id)
      })
      this.requestFilterLabels()
    },
    requestFilterLabels() {
      return new Promise(async (resolve, reject) => {
        try {
          // Get the label info for the filter popovers
          let inventoryBinLabels = await this.$apiService.coreService.getLabelsForType('inventoryBin')
          this.$store.commit({
            type: 'label/updateLabels',
            labelType: 'inventoryBin',
            labelList: inventoryBinLabels.data
          })

          return resolve()
        } catch(err) {
          return reject(err)
        }
      })      
    },

    // Stock Categories
    getStockCategoryList(parentCategoryId, level = 0) {
      var outputList = []
      for(var category of this.$data.stockCategoryList) {
        if(category.parentCategoryId == parentCategoryId) {
          outputList.push({
            id: category.id,
            name: category.name,
            stockType: category.stockType,
            parentCategoryId: category.parentCategoryId,
            indentDisplay: _.repeat('&nbsp;', level*2) + (level > 0 ? '<i class="fas fa-caret-right"></i>' : ''),
            isActive: category.isActive,
            level: level
          })

          outputList = outputList.concat(this.getStockCategoryList(category.id, level+1))
        }
      }
      return outputList
    },
    getNestedStockCategoryList(parentCategoryId, level = 0) {

      return this.$data.stockCategoryList
        .filter(category => category.parentCategoryId == parentCategoryId)
        .map(category => {
          var children = this.getNestedStockCategoryList(category.id, level+1)

          if(this.stockCategoryShowStock && !this.stockCategoryEditing) {
            let catStockList = this.stockList.filter(stock => stock.stockCategoryId == category.id && stock.isActive)

            children = _.concat(children, catStockList.map(stock => {
              return {
                isStock: true,
                id: stock.id,
                code: stock.code,
                name: stock.name,
                level: level,
                children: []
              }
            }))
          }

          return {
            isCategory: true,
            id: category.id,
            name: category.name,
            stockType: category.stockType,
            uomType: category.uomType,
            parentCategoryId: category.parentCategoryId,
            isActive: category.isActive,
            level: level,
            children: children
          }
        })
    },

    newStockCategory(parentCategoryId, stockType) {
      this.displayModal(StockCategoryDialog, { 
        isCreateMode: true,
        parentCategoryId: parentCategoryId,
        stockType: stockType
      })
      .then((success) => {
        if(success) {
          this.successToast('Stock Category Added')
          this.reloadData()
        }
      })
    },
    editStockCategory(stockCategory) {
      this.displayModal(StockCategoryDialog, { 
        isCreateMode: false
      }, stockCategory)
      .then((success) => {
        if(success) {
          this.successToast('Stock Category Updated')
          this.reloadData()
        }
      })
    },    
    setStockCategoryActive(stockCategoryId, isActive) {
      this.$apiService.inventoryService.updateStockCategory(stockCategoryId, { isActive: isActive })
      .then(response => {
        this.successToast('Stock Category Updated')
        this.reloadData()
      })
    },
    startCategoryEditing() {
      this.stockCategoryEditing = true

      this.$data.nestedCategoryList = this.getNestedStockCategoryList(null)
    },
    cancelCategoryEditing() {
      this.stockCategoryEditing = false

      // Refresh the stock category list      
      this.$apiService.inventoryService.getStockCategoryList().then(response => {
        this.$data.stockCategoryList = response.data
        this.$data.nestedCategoryList = this.getNestedStockCategoryList(null)
      })
    },
    submitCategoryEditing() {
      this.$data.stockCategorySubmitting = true

      var categoryList = this.generateCategoryListForUpdate(this.$data.nestedCategoryList, null)

      this.$apiService.inventoryService.updateStockCategoryBatch({ 'categories': categoryList })
      .then(response => {
        this.stockCategoryEditing = false
      })
      .catch(err => {
        this.displayNetworkError(err, 'Failed to update', 'Error while attempting to update stock categories')                
      })
      .finally(() => {
        
        this.$data.stockCategorySubmitting = false
      })
    },
    generateCategoryListForUpdate(list, parentId) {
      if(!list)
        return []

      var outputList = []
      for(var [index, item] of list.entries()) {
        outputList.push({
          id: item.id,
          parentCategoryId: parentId,
          sortOrder: index,
        })

        outputList = _.concat(outputList, this.generateCategoryListForUpdate(item.children, item.id))
      }

      return outputList
    },

    // Stock Container
    newStockContainer() {
      this.displayModal(StockContainerDialog, { 
        isCreateMode: true
      })
      .then((success) => {
        if(success) {
          this.successToast('Stock Container Type Added')
          this.reloadData()
        }
      })
    },
    editStockContainer(stockContainer) {
      this.displayModal(StockContainerDialog, { 
        isCreateMode: false
      }, stockContainer)
      .then((success) => {
        if(success) {
          this.successToast('Stock Container Type Updated')
          this.reloadData()
        }
      })
    },    
    setStockContainerActive(stockContainerId, isActive) {
      if(!isActive) {
        this.confirmDeleteMessage('Set Container to Inactive?', 'Setting a stock container to inactive will cause issues with any resources (eg, purchase orders) currently using this container')
        .then(success => {
          if(success) {
            this.$apiService.inventoryService.updateStockContainer(stockContainerId, { isActive: isActive })
            .then(response => {
              this.successToast('Stock Container Type Updated')
              this.reloadData()
            })
          }
        })

      } else {
        this.$apiService.inventoryService.updateStockContainer(stockContainerId, { isActive: isActive })
        .then(response => {
          this.successToast('Stock Container Type Updated')
          this.reloadData()
        })
      }
    },
    cancelStockContainerEditing() {
      this.stockContainerEditing = false

      // Refresh the stock container list
      this.$apiService.inventoryService.getStockContainerList().then(response => {
        this.$data.stockContainerList = response.data

        this.$nextTick(() => {
          // JB: This is hacky, but cannot seem to get the table data to reset after sortable()
          this.$data.stockContainerKey++
        })
      })
    },
    submitStockContainerEditing() {
      this.$data.stockContainerSubmitting = true

      var stockContainerBatchData = this.stockContainerOrder.map((containerId, index) => {
        return {
          id: containerId,
          sortOrder: index
        }
      })

      this.$apiService.inventoryService.updateStockContainerBatch({ 'containers': stockContainerBatchData })
      .then(response => {
        this.stockContainerEditing = false
      })
      .catch(err => {
        this.displayNetworkError(err, 'Failed to update', 'Error while attempting to update stock container types')                
      })
      .finally(() => {
        this.$data.stockContainerSubmitting = false
      })
    },

    // General Config Settings
    getConfigSetting(settingKey, defaultValue = '') {
      if(this.inventorySettings.settings[settingKey] !== undefined)
        return this.inventorySettings.settings[settingKey]
      return defaultValue
    },
    getConfigSettingBoolean(settingKey, defaultValue = false) {
      if(this.inventorySettings.settings[settingKey] !== undefined)
        return this.parseBoolean(this.inventorySettings.settings[settingKey])
      return defaultValue
    },
    getConfigSettingInt(settingKey, defaultValue = 0) {
      if(this.inventorySettings.settings[settingKey] !== undefined)
        return parseInt(this.inventorySettings.settings[settingKey])
      return defaultValue
    },
    getConfigSettingNumber(settingKey, defaultValue = 0) {
      if(this.inventorySettings.settings[settingKey] !== undefined)
        return this.parseDecimal(this.inventorySettings.settings[settingKey])
      return defaultValue
    },
    setConfigSetting(settingKey, value) {
      this.inventorySettings.settings[settingKey] = value
    },

    submitInventoryConfigSettings() {
      this.$data.inventorySettings.submitting = true
      this.$apiService.inventoryService.updateConfigSettings({ settings: this.$data.inventorySettings.settings })
      .then(response => {
        this.$data.inventorySettings.editing = false
        this.successToast('Inventory Config Settings updated')
      })
      .catch(err => {
        this.$data.inventorySettings.form = this.processFormResponse(err)
      })
      .finally(() => {
        this.$data.inventorySettings.submitting = false
      })
    },
    cancelInventoryConfigSettings() {
      this.$data.inventorySettings.editing = false
      this.$apiService.inventoryService.getConfigSettings()
      .then(response => {
        this.$data.inventorySettings.settings = response.data
      })
    },

    // Inventory Bins
    addInventoryBin() {
      this.displayModal(InventoryBinDialog, { 
        isCreateMode: true
      })
      .then((success) => {
        if(success) {
          this.successToast('Inventory Bin Created')
          this.reloadData()
        }
      })
    },
    addInventoryBinBatch() {
      this.displayModal(InventoryBinBatchDialog)
      .then(() => {
        this.reloadData()
      })
    },
    printInventoryBinLabel(inventoryBin) {
      this.$apiService.inventoryService.generatePrintInventoryBinLabelURL(inventoryBin.id, { download: true, label: true, inventorybinlabel: true })
      .then(response => {
        window.location.href = response.data.url
      })
      .catch(err => {
        console.error(err)
        this.displayNetworkError(err, 'Print Error')
      })
    },
    editInventoryBin(inventoryBin) {
      this.displayModal(InventoryBinDialog, { 
        isCreateMode: false,
        inventoryBinId: inventoryBin.id
      })
      .then((success) => {
        if(success) {
          this.successToast('Inventory Bin Updated')
          this.reloadData()
        }
      })
    },
    setInventoryBinActive(inventoryBin, isActive) {
      this.$apiService.inventoryService.updateInventoryBin(inventoryBin.id, { isActive: isActive })
      .then(response => {
        this.successToast('Inventory Bin Updated')
        this.reloadData()
      })
    },

    viewInventoryBinContents(inventoryBin) {
      this.displayModal(InventoryBinContentsDialog, { 
        inventoryBinId: inventoryBin.id
      })
      .then(() => {
      })
    }

  }
}

</script>